import { useState } from 'react';
import { Mic, MicOff, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useVoiceInput } from '@/hooks/useVoiceInput';

/**
 * VoiceInputButton — drop-in mic button that dictates into a text field.
 *
 * Props:
 *   onTranscript(finalChunk: string, opts:{append:boolean})  — required
 *       Called when a final chunk is available.
 *   compact (bool)   — smaller icon-only variant for inline placement inside inputs
 *   className        — extra classes
 *   testid           — data-testid override; default "voice-input-btn"
 *
 * Never auto-submits — parent is fully in control of the target value.
 */
export default function VoiceInputButton({
  onTranscript,
  compact = false,
  className = '',
  testid = 'voice-input-btn',
  ariaLabel = 'Dictate with your voice',
}) {
  const [notifiedUnsupported, setNotifiedUnsupported] = useState(false);
  const v = useVoiceInput({
    onFinal: (chunk) => {
      onTranscript?.(chunk, { append: true });
    },
  });

  const toggle = async () => {
    if (v.listening) { v.stop(); return; }
    if (!v.supported) {
      if (!notifiedUnsupported) {
        toast("Voice input isn't supported in this browser yet. You can still type your response.");
        setNotifiedUnsupported(true);
      }
      return;
    }
    await v.start();
  };

  // Surface permission errors
  if (v.error === 'permission-denied' && !notifiedUnsupported) {
    toast.error('Microphone permission was denied. Enable it in your browser settings to dictate.');
    setNotifiedUnsupported(true);
  }

  const baseSize = compact ? 'w-8 h-8' : 'w-10 h-10';
  const icon = v.listening ? MicOff : Mic;
  const IconCmp = icon;
  const bg = v.listening ? 'bg-red-600 text-white' : 'bg-beige/80 hover:bg-beige text-navy/70';
  const pulse = v.listening ? 'animate-soft-pulse' : '';

  return (
    <button
      type="button"
      onClick={toggle}
      data-testid={testid}
      aria-label={v.listening ? 'Stop dictation' : ariaLabel}
      title={v.listening ? 'Listening… tap to stop' : (v.supported ? 'Tap to dictate' : 'Voice input not supported')}
      disabled={!v.supported && notifiedUnsupported}
      className={`${baseSize} rounded-full flex items-center justify-center transition-smooth ${bg} ${pulse} disabled:opacity-40 disabled:cursor-not-allowed ${className}`}
    >
      <IconCmp size={compact ? 14 : 16} strokeWidth={1.75} />
    </button>
  );
}
